import { createSignal } from "solid-js";

/**
 * User's ACTIVE organization — module-level shared state so that
 * ALL instances of `useActiveContext` (one per domain route + the
 * header switcher) react to the same change, persisted in localStorage
 * to survive reloads.
 *
 * The key is a `userId → scopeId` map (not a flat value) because the app is
 * multi-account: each account remembers ITS active organization, and switching
 * accounts does not inherit the pointer from another (same spirit as the
 * multi-location in comandero and the multi-company in stockpilot — guide `MULTI_ORG_SWITCHER`).
 *
 * `scopeId` can be:
 *   - the id of a real organization (inspection organization where the
 *     user is a member), or
 *   - the empty string `""` → **PERSONAL** workspace (`organization_id IS NULL`;
 *     resources only I see). Unlike stockpilot (where Personal is
 *     `organization_id = user_id`), in field-check personal uses
 *     NULL `organization_id` — the switcher sentinel is `""` (same criterion
 *     as the `OrgSelector` for "Shared with: Personal | <org>").
 *
 * The pointer is only a PREFERENCE: `useActiveContext` validates it against
 * orgs actually synced locally and falls back to "Personal" if it no longer exists (deleted org
 * / old data / not synced yet) — it never blocks (self-healing).
 */
const STORAGE_KEY = "fieldcheck.activeOrgByUser";

/** Sentinel for the Personal scope (resources with `organization_id IS NULL`). */
export const PERSONAL_SCOPE = "";

type ActiveMap = Readonly<Record<string, string>>;

/** Parses the persisted `userId → scopeId` map. Tolerates missing/corrupt JSON. */
export function parseActiveMap(raw: string | null | undefined): ActiveMap {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const entries = Object.entries(parsed).filter(
        (entry): entry is [string, string] => typeof entry[1] === "string",
      );
      return Object.fromEntries(entries);
    }
  } catch {
    // localStorage can fail (private mode) or the JSON can be corrupt; the
    // in-memory state is enough and the fallback is "Personal".
  }
  return {};
}

/**
 * Resolves the ACTIVE `organization_id` (or `null` = Personal) from the
 * persisted pointer and the orgs present locally. PURE and testable:
 *   - no pointer or Personal pointer (`""`) ⇒ `null` (Personal),
 *   - pointer to an org that IS present locally ⇒ that id,
 *   - pointer to a missing org (deleted / not synced / old data) ⇒
 *     `null` (self-healing to Personal; never blocks).
 */
export function resolveActiveOrgId(
  stored: string | null | undefined,
  orgIds: string[],
): string | null {
  if (!stored) {
    return null;
  }
  return orgIds.includes(stored) ? stored : null;
}

function readPersisted(): ActiveMap {
  return parseActiveMap(globalThis.localStorage?.getItem(STORAGE_KEY));
}

const [activeMap, setActiveMap] = createSignal<ActiveMap>(readPersisted());

/**
 * Epoch of an EXPLICIT organization change. It increments ONLY when the
 * user intentionally changes organizations (`setActiveScopeIdFor`), never during
 * scope settling at boot (guest→real user, late sync…).
 *
 * The isolation guard (`useResetOnOrgSwitch`) watches this epoch to
 * discard pointers to an open entity from a deep link (`?site=`, `?new=`) when
 * changing organizations, WITHOUT the risk of deleting a legitimate deep link
 * during startup (which would happen if watching the scope directly).
 */
const [switchEpoch, setSwitchEpoch] = createSignal(0);

/** User's active organization pointer (`""` Personal; may point to a nonexistent organization). */
export function activeScopeIdFor(userId: string): string {
  return activeMap()[userId] ?? PERSONAL_SCOPE;
}

/** Sets (and persists) the user's active organization and signals an explicit change. */
export function setActiveScopeIdFor(userId: string, scopeId: string): void {
  const current = activeMap()[userId] ?? PERSONAL_SCOPE;
  const next = { ...activeMap(), [userId]: scopeId };
  setActiveMap(next);
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Best-effort persistence; the in-memory signal has already changed.
  }
  // It only counts as a "switch" if the destination really changes — so re-setting
  // the same scope (e.g. reactivating the already active one) does not trigger the guard.
  if (current !== scopeId) {
    setSwitchEpoch((n) => n + 1);
  }
}

/** Reactive accessor for the explicit organization-change epoch (for the guard). */
export function orgSwitchEpoch(): number {
  return switchEpoch();
}

/** Tests only: reset the in-memory state by re-reading localStorage. */
export function resetActiveOrgStateForTests(): void {
  setActiveMap(readPersisted());
}
